import React, { useEffect, useState } from 'react';
import { SectionLayout } from '@/components/shared/section-layout';
import { Trash2, Download, Square, Upload } from 'lucide-react';
import { NewItemButton, SearchButton, ThreeDotMenuButton } from '@/styles/StandardButtons';
import { DailyLogModal } from './components/DailyLogModal';
import { DailyLogsList } from './components/DailyLogsList';
import { NewDailyLogInput, DailyLog } from './types';
import { useDailyLogs } from './store';
import { fetchProjectLookup, fetchTeamMemberLookup } from '@/lib/entity-api';
import { exportEntityCsv } from '@/lib/csv';
import { useUser } from '@/contexts/UserContext';
import { useLocation } from 'react-router-dom';
import { trackDailyLogCreated, trackDailyLogUpdated, trackDailyLogViewed, trackButtonClick, trackFormSubmission } from '@/lib/posthog-events';

export function DailyLogsPage() {
  const { companyId, user } = useUser();
  const { addDailyLog, updateDailyLog, items: logs, hasMore, isLoading, loadMore, exportRows, setSearchInput, queryKey } = useDailyLogs();
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<DailyLog | null>(null);
  const [projects, setProjects] = useState<{ id: string; name: string }[]>([]);
  const [projectIdToName, setProjectIdToName] = useState<Map<string, string>>(new Map());
  const [currentAuthor, setCurrentAuthor] = useState<string>("Unknown User");
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const location = useLocation();

  // Check if we should open the form from navigation
  useEffect(() => {
    if (location.state?.openForm) {
      setCreateOpen(true);
      // Clear the state to prevent reopening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // The list is a new query when the project or the search changes: rows selected in the previous one are gone.
  useEffect(() => {
    setSelectedRows([]);
  }, [queryKey]);

  useEffect(() => {
    if (companyId && user) {
      (async () => {
        let projData: Awaited<ReturnType<typeof fetchProjectLookup>> = [];
        try {
          // The company's active projects and people come from the lookups API.
          const [projectsLookup, members] = await Promise.all([fetchProjectLookup(), fetchTeamMemberLookup()]);
          projData = projectsLookup;
          setProjects(projData);
          setProjectIdToName(new Map(projData.map((p) => [p.id, p.name] as const)));
          const fullNameFromTable = members.find((m) => m.user_id === user.id)?.full_name || '';
          const fullNameFromMeta = (user.user_metadata as any)?.full_name as string | undefined;
          setCurrentAuthor(fullNameFromTable || fullNameFromMeta || user.email || 'Unknown User');
        } catch (error) {
          console.error('Error loading daily log options:', error);
        }

        // Track daily logs page view with PostHog
        trackDailyLogViewed({
          log_count: logs.length,
          project_count: projData.length,
          company_id: companyId,
          page: 'daily_logs'
        });
      })();
    }
  }, [companyId, user, logs.length]);

  async function handleCreate(data: NewDailyLogInput) {
    console.log('🚀 === PAGE HANDLE CREATE STARTED ===');
    console.log('📋 Create data:', data);
    console.log('👤 Current author:', currentAuthor);
    
    try {
      console.log('💾 Calling addDailyLog...');
      const created = await addDailyLog(data, currentAuthor);
      console.log('✅ Daily log created:', created);
      
      // Track successful daily log creation with PostHog
      trackDailyLogCreated({
        log_id: created?.id,
        project_id: data.projectId,
        project_name: projectIdToName.get(data.projectId) || 'Unknown Project',
        date: data.date,
        weather_mode: data.weatherMode,
        has_notes: !!data.notes,
        has_issues: !!data.issues,
        attachment_count: data.attachments?.length || 0,
        company_id: companyId,
        created_by: user?.id
      });

      // Track successful form submission
      trackFormSubmission({
        form_type: 'daily_log_creation',
        success: true
      });
      
      return created; // Return the created daily log so the modal can use its ID
    } catch (error) {
      console.error('❌ Error in handleCreate:', error);
      
      // Track failed daily log creation with PostHog
      trackFormSubmission({
        form_type: 'daily_log_creation',
        success: false,
        error_message: error instanceof Error ? error.message : 'Failed to create daily log'
      });
      
      throw error;
    }
  }

  async function handleEdit(data: NewDailyLogInput) {
    console.log('🚀 === PAGE HANDLE EDIT STARTED ===');
    console.log('📋 Edit data:', data);
    console.log('✏️ Editing log:', editing);
    
    if (editing) {
      try {
        console.log('💾 Calling updateDailyLog...');
        const updated = await updateDailyLog(editing.id, data);
        console.log('✅ Daily log updated:', updated);
        
        // Track successful daily log update with PostHog
        trackDailyLogUpdated({
          log_id: editing.id,
          project_id: data.projectId,
          project_name: projectIdToName.get(data.projectId) || 'Unknown Project',
          date: data.date,
          weather_mode: data.weatherMode,
          has_notes: !!data.notes,
          has_issues: !!data.issues,
          attachment_count: data.attachments?.length || 0,
          company_id: companyId,
          updated_by: user?.id
        });

        // Track successful form submission
        trackFormSubmission({
          form_type: 'daily_log_update',
          success: true
        });
        
        return updated; // Return the updated daily log
      } catch (error) {
        console.error('❌ Error in handleEdit:', error);
        
        // Track failed daily log update with PostHog
        trackFormSubmission({
          form_type: 'daily_log_update',
          success: false,
          error_message: error instanceof Error ? error.message : 'Failed to update daily log'
        });
        
        throw error;
      }
    }
    return undefined;
  }

  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) {
      alert('Please select at least one daily log to delete.');
      return;
    }

    if (!window.confirm(`Delete ${selectedRows.length} daily log(s)? This action cannot be undone.`)) {
      return;
    }

    if (!user || !companyId) return;

    try {
      let successCount = 0;
      let failureCount = 0;

      for (const logId of selectedRows) {
        try {
          const response = await fetch(`/api/entities/daily_log/${logId}`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: user.id,
              company_id: companyId
            })
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to delete daily log');
          }

          successCount++;
        } catch (deleteError) {
          console.error('Error deleting daily log:', deleteError);
          failureCount++;
        }
      }

      if (successCount > 0) {
        // Refresh the logs list
        window.location.reload(); // Simple refresh since we don't have a refetch function exposed
        setSelectedRows([]);
        setIsSelectionMode(false);
      }

      if (failureCount > 0) {
        alert(`Deleted ${successCount} daily log(s). ${failureCount} failed.`);
      } else if (successCount > 0) {
        alert(`Deleted ${successCount} daily log(s) successfully.`);
      }
    } catch (error) {
      console.error('Error deleting daily logs:', error);
      alert('Failed to delete daily logs. Please try again.');
    }
  };

  // Export to CSV: the logs selected, or -- with nothing selected -- EVERY daily log of the current project + search,
  // read by the backend (not only the pages loaded on screen).
  const handleExportSelected = () => exportEntityCsv(exportRows, selectedRows, 'daily_logs', 'daily logs');

  const handleSelect = () => {
    setIsSelectionMode(true);
  };

  const handleSelectAll = () => {
    setIsSelectionMode(true);
    setSelectedRows(logs.map(log => log.id));
  };

  const handleCancelSelection = () => {
    setIsSelectionMode(false);
    setSelectedRows([]);
  };

  const handleImport = () => {
    // Import functionality can be implemented later
    console.log('Import clicked');
    alert('Import functionality coming soon!');
  };

  // Menu items for ThreeDotMenuButton
  const menuItems = [
    {
      label: 'Select',
      icon: <Square className="w-4 h-4" />,
      onClick: handleSelect,
    },
    {
      label: 'Import',
      icon: <Upload className="w-4 h-4" />,
      onClick: handleImport,
    },
    {
      label: 'Export',
      icon: <Download className="w-4 h-4" />,
      onClick: handleExportSelected,
    },
    {
      label: 'Delete',
      icon: <Trash2 className="w-4 h-4" />,
      onClick: handleDeleteSelected,
      destructive: true,
      disabled: selectedRows.length === 0,
    },
  ];

  return (
    <SectionLayout title="Daily Logs">
      <div className="flex items-center justify-between">
        <div className="text-gray-600" />
        <div className="flex items-center gap-2">
          <NewItemButton
            label="New"
            onClick={() => {
            trackButtonClick('new_daily_log_button', { context: 'daily_logs' });
            setCreateOpen(true);
            }}
          />
          <SearchButton
            onSearch={setSearchInput}
            placeholder="Search"
          />
          <ThreeDotMenuButton items={menuItems} />
        </div>
      </div>

      <div className="mt-4">
        <DailyLogsList
          logs={logs}
          hasMore={hasMore}
          isLoading={isLoading}
          loadMore={loadMore}
          onOpen={(log) => { setEditing(log); setEditOpen(true); }}
          projectIdToName={projectIdToName}
          selectedRows={selectedRows}
          isSelectionMode={isSelectionMode}
          onSelectRow={(logId, checked) => {
            if (checked) {
              setSelectedRows(prev => [...prev, logId]);
            } else {
              setSelectedRows(prev => prev.filter(id => id !== logId));
            }
          }}
          onSelectAll={(checked) => {
            if (checked) {
              setSelectedRows(logs.map(log => log.id));
            } else {
              setSelectedRows([]);
            }
          }}
          onCancelSelection={handleCancelSelection}
        />
      </div>

      <DailyLogModal
        open={createOpen}
        mode="create"
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
        projects={projects}
      />

      <DailyLogModal
        open={editOpen}
        mode="edit"
        initial={editing ?? undefined}
        onClose={() => { setEditOpen(false); setEditing(null); }}
        onSubmit={handleEdit}
        projects={projects}
      />
    </SectionLayout>
  );
}